import React from 'react';
import ObservationEntryTable from './ObservationEntryTable';

export const StackedResultEntry = ({
  tests = [],
  selectedTestIds = new Set(),
  onToggleTestCheckbox,
  testObservationsMap = {},
  instrumentResultsMap = {},
  onObservationChange,
  onRemarksChange,
  onFillDefaults,
  onOpenParameterHistory,
}) => {
  return (
    <div className="path-layout-stacked">
      {/* Global Stack Summary Header */}
      <div className="path-stack-controls-bar">
        <span className="info-text">
          Showing {tests.length} Complete Investigation{tests.length > 1 ? 's' : ''} (Fully Expanded Stacked View)
        </span>
      </div>

      {/* Stacked Fully-Expanded Cards */}
      <div className="path-stacked-cards">
        {tests.map((test) => {
          const isChecked = selectedTestIds.has(test.testId);
          const isApproved = test.approved === 1;
          const isDraft = test.resultFlag === 1 && test.approved === 0;
          const isOutsourced = Boolean(test.isOutsourced);
          const isRejected = Boolean(test.isRejected || test.isSampleCollected === 'R');
          const obsList = testObservationsMap[test.testId] || [];
          const testDisplayName = test.investigationName || test.testName || `Test #${test.testId}`;

          const cardClass = isApproved
            ? 'card-approved'
            : isDraft
            ? 'card-draft'
            : isRejected
            ? 'card-rejected'
            : isOutsourced
            ? 'card-outsourced'
            : 'card-pending';

          return (
            <div
              key={test.testId}
              className={`path-stacked-card ${cardClass}`}
            >
              {/* Card Static Section Header (Non-collapsible) */}
              <div className="path-stacked-card-header path-stacked-static-header">
                <div className="title-area">
                  <label
                    className="checkbox-wrap"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={isOutsourced || isRejected}
                      onChange={() => onToggleTestCheckbox(test.testId)}
                      className="path-checkbox-lg"
                      title={isOutsourced || isRejected ? 'Non-editable investigation' : 'Select test'}
                    />
                  </label>
                  <span className="test-name">{testDisplayName}</span>
                  <span className="path-dept-tag">{test.departmentName || 'Pathology'}</span>
                  {test.barcodeNo && (
                    <span className="path-barcode-pill">🏷️ {test.barcodeNo}</span>
                  )}
                </div>

                <div className="status-area">
                  <span
                    className={`path-status-badge ${
                      isApproved
                        ? 'status-approved'
                        : isDraft
                        ? 'status-draft'
                        : isRejected
                        ? 'status-rejected'
                        : isOutsourced
                        ? 'status-outsourced'
                        : 'status-pending'
                    }`}
                  >
                    {isApproved
                      ? 'Approved'
                      : isDraft
                      ? 'Draft Saved'
                      : isRejected
                      ? 'Sample Rejected'
                      : isOutsourced
                      ? 'Outsourced'
                      : 'Pending Entry'}
                  </span>
                  <span className="path-obs-count">({obsList.length} params)</span>
                </div>
              </div>

              {/* Outsource Alert Banner */}
              {isOutsourced && (
                <div className="path-alert-banner path-alert-banner-outsource" style={{ margin: '12px 16px 0 16px' }}>
                  <div className="path-alert-banner-icon">🌐</div>
                  <div className="path-alert-banner-body">
                    <div className="path-alert-banner-title">
                      OUTSOURCED INVESTIGATION — {test.referenceLabName || 'Partner Reference Laboratory'}
                    </div>
                    <div className="path-alert-banner-desc">
                      This sample was outsourced to <strong>{test.referenceLabName || 'External Partner'}</strong>.
                      {test.outsourceStatus && (
                        <span className="path-outsource-status-badge">Status: {test.outsourceStatus}</span>
                      )}
                    </div>
                    {test.externalResultSummary && (
                      <div className="path-outsource-summary-panel">
                        <span className="summary-title">Partner Result Summary:</span>
                        <p className="summary-text">{test.externalResultSummary}</p>
                      </div>
                    )}
                    {test.externalAttachmentUrl && (
                      <div className="path-outsource-attachment-row">
                        <a
                          href={test.externalAttachmentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="path-attachment-pill"
                        >
                          📄 View Partner Report Document ↗
                        </a>
                      </div>
                    )}
                    <div className="path-freeze-notice">
                      🔒 Result entry is frozen. Outsource report results and verification are handled via the Outsource Module.
                    </div>
                  </div>
                </div>
              )}

              {/* Rejection Alert Banner */}
              {isRejected && (
                <div className="path-alert-banner path-alert-banner-rejected" style={{ margin: '12px 16px 0 16px' }}>
                  <div className="path-alert-banner-icon">🚫</div>
                  <div className="path-alert-banner-body">
                    <div className="path-alert-banner-title">SPECIMEN REJECTED</div>
                    <div className="path-alert-banner-desc">
                      This specimen was rejected during reception/collection.
                      {test.rejectionReason && (
                        <span className="path-rejection-reason-pill">Reason: {test.rejectionReason}</span>
                      )}
                    </div>
                    <div className="path-freeze-notice">
                      🔒 Result entry is frozen. A fresh specimen collection (re-draw) is required to process this investigation.
                    </div>
                  </div>
                </div>
              )}

              {/* Complete Result Form (Always fully expanded) */}
              <div className="path-stacked-card-body">
                <ObservationEntryTable
                  testId={test.testId}
                  observations={obsList}
                  instrumentResultsMap={instrumentResultsMap?.[test.testId] || instrumentResultsMap}
                  onObservationChange={onObservationChange}
                  onRemarksChange={onRemarksChange}
                  onFillDefaults={onFillDefaults}
                  onOpenParameterHistory={onOpenParameterHistory}
                  readOnly={test.approved === 1 || isOutsourced || isRejected}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StackedResultEntry;
